import {areSetsEqual, compact, isSubset} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  findAllDownstreamMappableNodeIds,
  findAllUpstreamMappableNodeIds,
  isMappableNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {isRouterNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {
  EdgeIdsToEdgesMap,
  makeRouterConfig,
} from 'processes/graph-builder-processes/flowchart-viz/util/makeProcessFlowchartInteractiveNode';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {makeDataFlowEdgeHandle, getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import isLookupConfigCustomMapping from './isLookupConfigCustomMapping';
import {populateMapFromStartSectionRows} from './mapFromStartUtils';
import {
  ProcessFlowchartEdgeType,
  FlowchartStaticEdgeData,
  CustomMappingData,
  TaskSchemaNodeSectionType,
  isCustomMappingDataEmpty,
  TransitionSource,
} from './processFlowchartConfigEditorConstants';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartConfigVersionGQL,
  FieldDefinitionGraphQL,
  FieldMapperConfigGQL,
  TopLevelFieldMapperConfigGQL,
  InboundSchemaMapperGQL,
  SchemaMapperDependencyGQL,
} from './processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  RouterStaticNodeData,
  TaskSchemaStaticNodeData,
  isStaticTaskSchemaNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromRouterConfig,
  createEndNodeStaticData,
  createStaticNodeDataFromTaskDefinitionVersion,
} from './processFlowchartConfigEditorUtils';
import {getDefaultTransitionSource} from './TransitionSourceUtils';
import {getFieldPairKey, getOrCreateTransition} from './types/transition/transitionKeyUtils';
import {Transition} from './types/transition/TransitionTypes';

export function generateDeterministicEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `edge-${sourceNodeId}-${targetNodeId}`;
}

export function buildControlFlowEdge({
  clientId,
  sourceNodeId,
  targetNodeId,
  label,
}: {
  clientId?: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
}): FlowchartStaticEdgeData {
  return {
    id: clientId ?? generateDeterministicEdgeId(sourceNodeId, targetNodeId),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: defaultNodeHandleIdGenerator(sourceNodeId, 'source'),
    targetHandle: defaultNodeHandleIdGenerator(targetNodeId, 'target'),
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    label,
  };
}

export function buildDataFlowEdge({
  sourceNodeId,
  targetNodeId,
  fromFieldId,
  fromFieldSectionType,
  toFieldId,
  toFieldSectionType,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  fromFieldId: string;
  fromFieldSectionType: TaskSchemaNodeSectionType;
  toFieldId: string;
  toFieldSectionType: TaskSchemaNodeSectionType;
}): FlowchartStaticEdgeData {
  return {
    id: uuidv4(),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: makeDataFlowEdgeHandle({
      nodeId: sourceNodeId,
      fieldClientId: fromFieldId,
      sectionType: fromFieldSectionType,
      handleType: 'source',
    }),
    targetHandle: makeDataFlowEdgeHandle({
      nodeId: targetNodeId,
      fieldClientId: toFieldId,
      sectionType: toFieldSectionType,
      handleType: 'target',
    }),
    type: ProcessFlowchartEdgeType.DATA_FLOW,
  };
}

/**
 * Get the first field id from a lookup config with a SCHEMA_FIELD or PROCESS_TASK_DEFINITION_FIELD lookup step.
 *
 * @param lookupConfig The lookup config to get the first field id from.
 * @returns The first field id from the lookup config, or null if no field id is found.
 */

export function getFirstFieldIdFromLookupConfig(lookupConfig: LookupConfigSpec): string | null {
  const lookupStep = lookupConfig.lookupSteps.find(
    (lookupStep) =>
      lookupStep.type === LookupStepType.SCHEMA_FIELD ||
      lookupStep.type === LookupStepType.PROCESS_TASK_DEFINITION_FIELD
  );
  if (lookupStep == null) {
    return null;
  }

  if (lookupStep.type === LookupStepType.SCHEMA_FIELD) {
    if (lookupStep.schemaField == null || !APIUtil.isSchemaField(lookupStep.schemaField)) {
      return null;
    }
    return lookupStep.schemaField;
  } else {
    return lookupStep.processTaskDefinitionFieldIdentifier;
  }
}

const getNodeIdPairKeyForEdgeLabel = (fromNodeId: string, toNodeId: string): string =>
  `${fromNodeId}-${toNodeId}`;

export interface StaticFlowchartData {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  transitions: Record<string, Transition>;
  didInitialConfigHaveConditionalMapping: boolean;
  hasChainedRouters: boolean;
  laBlockingReason: string | null;
  cidToIdMap: CidIdMap;
}

interface BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs {
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
}

class BuildStaticDataFromFlowchartConfigGraphQLRunner {
  private readonly flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  private readonly rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private readonly rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  private readonly allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  private readonly flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;

  private nodeClientIdToTaskDefinitionVersion: Record<string, ProcessTaskDefinitionVersionGraphQL>;

  private controlFlowEdges: Array<FlowchartStaticEdgeData>;
  private dataFlowEdges: Array<FlowchartStaticEdgeData>;
  private nodesStaticData: Array<StaticFlowchartNodeData>;
  private nodesById: Record<string, StaticFlowchartNodeData>;
  private edgesStaticData: Array<FlowchartStaticEdgeData>;
  private mapFromStartFieldIdsByNode: Record<string, Array<string>>;
  private transitions: Record<string, Transition>;
  private nodeIdPairToEdgeLabel: Record<string, string>;
  private cidToIdMap: CidIdMap;

  // Maps from field identifier to client identifier for data flow edges
  private fieldIdentifierToFieldClientId: Record<string, string>;

  constructor(args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs) {
    this.flowchartConfigVersion = args.flowchartConfigVersion;
    this.rootTaskSchema = args.rootTaskSchema;
    this.rootTaskDefinition = args.rootTaskDefinition;
    this.allTaskSchemas = args.allTaskSchemas;
    this.flowchartEditorConfigurator = args.flowchartEditorConfigurator;

    this.cidToIdMap = {};

    this.nodeClientIdToTaskDefinitionVersion = {};
    this.controlFlowEdges = [];
    this.dataFlowEdges = [];
    this.fieldIdentifierToFieldClientId = {};

    this.nodesStaticData = [];
    this.nodesById = {};
    this.edgesStaticData = [];
    this.mapFromStartFieldIdsByNode = {};
    this.transitions = {};
    this.nodeIdPairToEdgeLabel = {};
  }

  run(): StaticFlowchartData {
    this.extractTaskDefinitionVersions();
    this.buildFieldIdentifierToClientIdMapping();
    this.convertFlowchartConfigVersionToNodes();
    this.buildNodesById();
    this.convertFlowchartConfigVersionToEdges();
    this.buildTransitionsFromUnreferencedSchemaMapperConfigs();
    this.setFallbackTransitionSources();
    this.markOptionalTransitions();
    const hasChainedRouters = this.getHasChainedRouters();
    const laBlockingReason = this.getLABlockingReason();

    return {
      nodesStaticData: this.nodesStaticData,
      edgesStaticData: this.edgesStaticData,
      availableTaskSchemas: accessibleObjs(this.flowchartConfigVersion?.availableSchemas ?? []),
      transitions: this.transitions,
      didInitialConfigHaveConditionalMapping: this.getDidInitialConfigHaveConditionalMapping(),
      hasChainedRouters,
      laBlockingReason,
      cidToIdMap: this.cidToIdMap,
    };
  }

  /**
   * For single-source requests, checks whether an existing transition (e.g. a merging transition)
   * already covers this source-target pair before creating a new one. This works because we do not allow
   * transitions that are a subset of each other.
   */
  private getOrCreateTransition(sourcePTDVIds: Array<string>, targetPTDVId: string): Transition {
    if (sourcePTDVIds.length === 1) {
      for (const transition of Object.values(this.transitions)) {
        if (transition.targetPTDVId === targetPTDVId && transition.sourcePTDVIds.includes(sourcePTDVIds[0])) {
          return transition;
        }
      }
    }

    return getOrCreateTransition({
      sourcePTDVIds,
      targetPTDVId,
      transitions: this.transitions,
      isPlanned: true,
    }).transition;
  }

  private buildNodesById(): void {
    for (const node of this.nodesStaticData) {
      this.nodesById[node.id] = node;
    }
  }

  private getNodeById(nodeId: string): StaticFlowchartNodeData | undefined {
    return this.nodesById[nodeId];
  }

  private getTaskSchemaNodeById(nodeId: string): TaskSchemaStaticNodeData | undefined {
    const node = this.nodesById[nodeId];
    return node && isStaticTaskSchemaNodeData(node) ? node : undefined;
  }

  private getRootNode(): TaskSchemaStaticNodeData | undefined {
    return this.nodesStaticData.find(
      (node): node is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
  }

  private getHasChainedRouters(): boolean {
    for (const edge of this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs ?? []) {
      const sourceNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.fromNodeConfig.id
      );
      const targetNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.toNodeConfig.id
      );
      if (sourceNode == null || targetNode == null) {
        continue;
      }
      if (isRouterNodeDetailsGQL(sourceNode.nodeDetails) && isRouterNodeDetailsGQL(targetNode.nodeDetails)) {
        return true;
      }
    }
    return false;
  }

  private getHasUndrawnMapperConfigs(): boolean {
    const schemaMapperConfigs = this.flowchartConfigVersion?.schemaMapperConfigs ?? [];
    const schemaMapperConfigPairs = new Set<string>();
    const controlFlowEdgePairs = new Set<string>();
    for (const schemaMapperConfig of schemaMapperConfigs) {
      if (isInaccessible(schemaMapperConfig.toProcessTaskDefinitionVersion)) {
        continue;
      }
      // Handle all source configs (supports merging transitions with multiple sources)
      for (const sourceConfig of schemaMapperConfig.schemaMapperSourceConfigs) {
        if (!isInaccessible(sourceConfig.processTaskDefinitionVersion)) {
          schemaMapperConfigPairs.add(
            `${sourceConfig.processTaskDefinitionVersion.id}-${schemaMapperConfig.toProcessTaskDefinitionVersion.id}`
          );
        }
      }
    }

    for (const node of this.nodesStaticData.filter((node) => isMappableNode({id: node.id, data: node}))) {
      const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((node) => ({id: node.id, data: node})), edges: this.edgesStaticData},
        node.id
      );
      for (const upstreamMappableNodeId of upstreamMappableNodeIds) {
        controlFlowEdgePairs.add(
          `${this.nodeClientIdToTaskDefinitionVersion[upstreamMappableNodeId].id}-${this.nodeClientIdToTaskDefinitionVersion[node.id].id}`
        );
      }
    }

    // When the transitions sidepanel is enabled, optional transitions create mapper configs
    // without control flow edges. Only block if planned edges are missing mapper configs.
    const hasUndrawnMappers = BenchlingConfig.WORKFLOWS_IMPROVED_OPTIONAL_TRANSITIONS
      ? !isSubset(controlFlowEdgePairs, schemaMapperConfigPairs)
      : !areSetsEqual(schemaMapperConfigPairs, controlFlowEdgePairs);
    if (hasUndrawnMappers) {
      this.logLABlockingReason('undrawn mapper configs');
    }
    return hasUndrawnMappers;
  }

  private logLABlockingReason(reason: string): void {
    logging.warn('LA horizontal flowchart loading with unsupported feature.', {
      extra: {
        flowchartConfigVersionId: this.flowchartConfigVersion?.id,
        rootTaskSchemaId: this.rootTaskSchema.id,
        feature: reason,
      },
    });
  }

  private getLABlockingReason(): string | null {
    const hasUndrawnMapperConfigs = this.getHasUndrawnMapperConfigs();
    const hasBlockingMerge =
      !BenchlingConfig.WORKFLOWS_IMPROVED_MERGING_TRANSITIONS && this.hasMergingTransitions();
    if (hasBlockingMerge) {
      this.logLABlockingReason('merging transitions');
    }
    if (hasUndrawnMapperConfigs || hasBlockingMerge) {
      return 'This flowchart isn’t editable in the limited release of the new configuration experience. Editing will be available when the new experience is generally available.';
    }
    return null;
  }

  private hasMergingTransitions(): boolean {
    return Object.values(this.transitions).some((transition) => transition.sourcePTDVIds.length > 1);
  }

  private extractTaskDefinitionVersions(): void {
    // TODO: See if this can be moved into the convertGQLFlowchartConfigToClientFlowchart function
    // so that we don't have to extract the task definition versions first
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const nodeDetails = nodeConfig.nodeDetails;
      if (
        nodeDetails.__typename === 'ProcessTaskNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessRootNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessOutputNodeDetailsNode'
      ) {
        const processTaskDefinitionVersion = nodeDetails.processTaskDefinitionVersion;
        if (isInaccessible(processTaskDefinitionVersion)) {
          continue;
        }
        this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id] = processTaskDefinitionVersion;
      }
    }
  }

  /**
   * Builds a mapping from field identifiers to their client identifiers, to be used when forming
   * data flow edges.
   */
  private buildFieldIdentifierToClientIdMapping(): void {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const taskDefinitionVersion = this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id];
      if (taskDefinitionVersion == null) {
        continue;
      }

      const inputFieldDefinitions = taskDefinitionVersion.inputFieldDefinitions ?? [];
      for (const fieldDefinition of inputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }

      const outputFieldDefinitions = taskDefinitionVersion.outputFieldDefinitions ?? [];
      for (const fieldDefinition of outputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }
    }
  }

  private convertFlowchartConfigVersionToNodes(): void {
    if (this.flowchartConfigVersion?.templateFlowchart == null) {
      const rootTaskDefinitionVersion = this.rootTaskDefinition.latestVersion;
      this.nodesStaticData = [
        createStaticNodeDataFromTaskDefinitionVersion({
          nodeClientId: 'root',
          taskDefinitionVersion: rootTaskDefinitionVersion,
          nodeDetailsType: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          allTaskSchemas: this.allTaskSchemas,
        }),
      ];
      if (this.rootTaskSchema.outputSchema != null) {
        const outputNode = createStaticNodeDataFromTaskDefinitionVersion({
          nodeClientId: 'output',
          taskDefinitionVersion: rootTaskDefinitionVersion,
          nodeDetailsType: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
          allTaskSchemas: this.allTaskSchemas,
        });
        this.nodesStaticData.push(outputNode);
      }
    } else {
      const existingNodeNames: Array<string> = [];

      this.nodesStaticData = compact(
        this.flowchartConfigVersion?.templateFlowchart.nodeConfigs.map((node) => {
          let clientNode: StaticFlowchartNodeData | null = null;
          switch (node.nodeDetails.__typename) {
            case 'ProcessRootNodeDetailsNode':
            case 'ProcessOutputNodeDetailsNode':
            case 'ProcessTaskNodeDetailsNode': {
              const taskDefinitionVersion = node.nodeDetails.processTaskDefinitionVersion;
              assert(!isInaccessible(taskDefinitionVersion), 'Task definition version is inaccessible');
              clientNode = createStaticNodeDataFromTaskDefinitionVersion({
                nodeClientId: node.id,
                taskDefinitionVersion,
                nodeDetailsType: node.nodeDetails.type,
                allTaskSchemas: this.allTaskSchemas,
                existingNodeNames,
              });
              break;
            }
            case 'ProcessRouterNodeDetailsNode': {
              const edgeIdsToEdges: EdgeIdsToEdgesMap = {};
              for (const edgeConfig of this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs ?? []) {
                if (edgeIdsToEdges[edgeConfig.id] == null) {
                  edgeIdsToEdges[edgeConfig.id] = [];
                }
                edgeIdsToEdges[edgeConfig.id].push({
                  clientId: edgeConfig.id,
                  fromNodeClientId: edgeConfig.fromNodeConfig.id,
                  toNodeClientId: edgeConfig.toNodeConfig.id,
                });
              }

              const routerConfig = makeRouterConfig(
                node.nodeDetails.name,
                node.nodeDetails.routerFunctions,
                edgeIdsToEdges
              );
              const routerNode = createStaticNodeDataFromRouterConfig(node.id, routerConfig);
              this.extractEdgeLabels(routerNode);
              clientNode = routerNode;
              break;
            }
            case 'ProcessEndNodeDetailsNode':
              clientNode = createEndNodeStaticData(node.id, node.name);
              break;
            default:
              assert(false, `Unexpected node type ${assertNever(node.nodeDetails)}`);
              return null;
          }
          if (clientNode == null) {
            return null;
          }
          this.cidToIdMap[node.id] = clientNode.id;
          // Track node name for subsequent incrementing
          existingNodeNames.push(clientNode.nodeName);
          return clientNode;
        })
      );
    }
  }

  private extractEdgeLabels(node: RouterStaticNodeData): void {
    for (const functionSpec of node.routerConfig.functionSpecs) {
      const fromNodeId = functionSpec.edgeConfigReference?.fromNodeClientId;
      const toNodeId = functionSpec.edgeConfigReference?.toNodeClientId;
      if (fromNodeId == null || toNodeId == null) {
        continue;
      }
      this.nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] = functionSpec.name;
    }
    if (node.routerConfig.defaultFunctionSpec != null) {
      const fromNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.fromNodeClientId;
      const toNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.toNodeClientId;
      if (fromNodeId != null && toNodeId != null) {
        this.nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] =
          node.routerConfig.defaultFunctionSpec.name;
      }
    }
  }

  private convertFlowchartConfigVersionToEdges(): void {
    this.controlFlowEdges = this.buildControlFlowEdges();
    this.dataFlowEdges = this.buildDataFlowEdges();
    // Filter out data flow edges with nonexistent handles
    this.dataFlowEdges = this.dataFlowEdges.filter((edge) => this.doesDataFlowEdgeHaveValidHandles(edge));
    this.edgesStaticData = [...this.controlFlowEdges, ...this.dataFlowEdges];
    if (
      this.flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.ALLOW_MAP_FROM_START)
    ) {
      this.updateNodesWithMapFromStartFields();
    }
  }

  /**
   * Check if a data flow edge's handles exist on the source and target nodes.
   */
  private doesDataFlowEdgeHaveValidHandles(edge: FlowchartStaticEdgeData): boolean {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || !edge.sourceHandle || !edge.targetHandle) {
      return false;
    }

    const sourceNode = this.getNodeById(edge.source);
    const targetNode = this.getNodeById(edge.target);

    if (
      !sourceNode ||
      !targetNode ||
      !isStaticTaskSchemaNodeData(sourceNode) ||
      !isStaticTaskSchemaNodeData(targetNode)
    ) {
      return false;
    }

    const sourceHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    return (
      this.doesFieldExistOnNode(sourceNode, sourceHandleInfo.fieldClientId, sourceHandleInfo.sectionType) &&
      this.doesFieldExistOnNode(targetNode, targetHandleInfo.fieldClientId, targetHandleInfo.sectionType)
    );
  }

  /**
   * Check if a field exists on a node's field definitions and is not archived.
   */
  private doesFieldExistOnNode(
    node: TaskSchemaStaticNodeData,
    fieldClientId: string,
    sectionType: TaskSchemaNodeSectionType
  ): boolean {
    const isOutputNode = node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    const isOutputSection = sectionType === TaskSchemaNodeSectionType.OUTPUTS;
    const isMapFromStartSection = sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;

    if (isMapFromStartSection) {
      const rootNode = this.getRootNode();
      if (!rootNode) {
        return false;
      }
      const rootFieldDefinitions = rootNode.taskDefinitionVersionSpec.inputFieldDefinitions;
      if (!rootFieldDefinitions) {
        return false;
      }
      return rootFieldDefinitions.some(
        (field) =>
          field.fieldClientIdentifier === fieldClientId &&
          (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
      );
    }

    const fieldDefinitions =
      isOutputNode || isOutputSection
        ? node.taskDefinitionVersionSpec.outputFieldDefinitions
        : node.taskDefinitionVersionSpec.inputFieldDefinitions;

    if (!fieldDefinitions) {
      return false;
    }

    // Check that the field exists and is not archived
    return fieldDefinitions.some(
      (field) =>
        field.fieldClientIdentifier === fieldClientId &&
        (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
    );
  }

  private buildControlFlowEdges(): Array<FlowchartStaticEdgeData> {
    return (
      this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs.map((edge) => {
        const newEdge = buildControlFlowEdge({
          clientId: edge.id,
          sourceNodeId: edge.fromNodeConfig.id,
          targetNodeId: edge.toNodeConfig.id,
          label:
            this.nodeIdPairToEdgeLabel[
              getNodeIdPairKeyForEdgeLabel(edge.fromNodeConfig.id, edge.toNodeConfig.id)
            ],
        });
        this.cidToIdMap[edge.id] = edge.id;
        return newEdge;
      }) ?? []
    );
  }

  private buildDataFlowEdges(): Array<FlowchartStaticEdgeData> {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    const dataFlowEdges: Array<FlowchartStaticEdgeData> = [];
    for (const nodeConfig of nodeConfigs) {
      for (const inboundSchemaMapper of nodeConfig.inboundSchemaMappers) {
        const mergingTransition = this.getOrCreateMergingTransitionForSchemaMapper(
          inboundSchemaMapper,
          nodeConfig.id
        );

        for (const schemaMapperDependency of inboundSchemaMapper.schemaMapperDependencies ?? []) {
          // If the dependency has a source config, we need to initialize the "transition source" (aka the
          // control flow token) based on the source.
          // This allows us to accurately set the transition source for mappers that do not involve any field
          // mapping.
          if (schemaMapperDependency.schemaMapperSourceConfig != null) {
            const targetNodeId = nodeConfig.id;
            const sourceNodeId = schemaMapperDependency.fromNodeConfig.id;
            const sourceNode = this.getNodeById(sourceNodeId);
            const targetNode = this.getNodeById(targetNodeId);
            if (
              sourceNode &&
              targetNode &&
              isStaticTaskSchemaNodeData(sourceNode) &&
              isStaticTaskSchemaNodeData(targetNode)
            ) {
              const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
              const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
              const transitionSource = this.getTransitionSourceFromMapperTokenType(
                schemaMapperDependency.schemaMapperSourceConfig.mapsFromType
              );
              const transition = this.getOrCreateTransition([sourcePTDVId], targetPTDVId);
              this.setTransitionSource(transition, sourcePTDVId, transitionSource);
            }
          }

          dataFlowEdges.push(
            ...compact(
              (schemaMapperDependency.fieldMapperConfigs ?? []).map((fieldMapperConfig) =>
                this.buildDataFlowEdgeFromFieldMapperConfig(
                  fieldMapperConfig,
                  schemaMapperDependency,
                  inboundSchemaMapper,
                  mergingTransition
                )
              )
            )
          );
        }
      }
    }
    return dataFlowEdges;
  }

  /**
   * Creates or retrieves a merging transition for a schema mapper config with multiple sources.
   * Returns null if the config has only one source (for single-source transitions) or cannot be found.
   */
  private getOrCreateMergingTransitionForSchemaMapper(
    inboundSchemaMapper: InboundSchemaMapperGQL,
    targetNodeId: string
  ): Transition | null {
    const schemaMapperConfigRef = inboundSchemaMapper.schemaMapperConfig;
    if (!schemaMapperConfigRef) {
      return null;
    }

    const fullSchemaMapperConfig = this.flowchartConfigVersion?.schemaMapperConfigs.find(
      (config) => config.id === schemaMapperConfigRef.id
    );
    if (!fullSchemaMapperConfig || fullSchemaMapperConfig.schemaMapperSourceConfigs.length <= 1) {
      return null;
    }

    const targetPTDVId = this.nodeClientIdToTaskDefinitionVersion[targetNodeId]?.id;
    if (!targetPTDVId) {
      return null;
    }

    const sourcePTDVIds: Array<string> = [];
    for (const sourceConfig of fullSchemaMapperConfig.schemaMapperSourceConfigs) {
      if (!isInaccessible(sourceConfig.processTaskDefinitionVersion)) {
        sourcePTDVIds.push(sourceConfig.processTaskDefinitionVersion.id);
      }
    }

    if (sourcePTDVIds.length <= 1) {
      return null;
    }

    return this.getOrCreateTransition(sourcePTDVIds, targetPTDVId);
  }

  private buildDataFlowEdgeFromFieldMapperConfig(
    fieldMapperConfig: FieldMapperConfigGQL,
    schemaMapperDependency: SchemaMapperDependencyGQL,
    inboundSchemaMapper: InboundSchemaMapperGQL,
    mergingTransition: Transition | null = null
  ): FlowchartStaticEdgeData | null {
    const fromNodeId = schemaMapperDependency.fromNodeConfig.id;
    const toNodeId = inboundSchemaMapper.toNodeConfig.id;

    const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(fieldMapperConfig);
    if (fromFieldInfo == null) {
      return null;
    }
    if (fromFieldInfo.fromFieldSectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      if (this.mapFromStartFieldIdsByNode[fromNodeId]) {
        this.mapFromStartFieldIdsByNode[fromNodeId].push(fromFieldInfo.fromFieldId);
      } else {
        this.mapFromStartFieldIdsByNode[fromNodeId] = [fromFieldInfo.fromFieldId];
      }
    }

    const mapsToType = inboundSchemaMapper.schemaMapperConfig?.mapsToType;
    if (mapsToType == null) {
      return null;
    }

    const toFieldSectionType =
      mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
        ? TaskSchemaNodeSectionType.OUTPUTS
        : TaskSchemaNodeSectionType.INPUTS;
    const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;
    const toFieldClientId = this.fieldIdentifierToFieldClientId[toFieldId];

    const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
    const fromFieldClientId = this.fieldIdentifierToFieldClientId[fromFieldId];

    const edge = buildDataFlowEdge({
      sourceNodeId: fromNodeId,
      targetNodeId: toNodeId,
      fromFieldId: fromFieldClientId,
      fromFieldSectionType,
      toFieldId: toFieldClientId,
      toFieldSectionType,
    });

    // This is populated in the case that we're not using a direct mapping.
    // Use the original field ID for custom mapping data
    const sourcePTDVId = this.nodeClientIdToTaskDefinitionVersion[fromNodeId]?.id;
    const targetPTDVId = this.nodeClientIdToTaskDefinitionVersion[toNodeId]?.id;

    if (sourcePTDVId && targetPTDVId) {
      // Use the merging transition if provided, otherwise create/get a single-source transition
      const transition = mergingTransition ?? this.getOrCreateTransition([sourcePTDVId], targetPTDVId);

      const transitionSource = this.getTransitionSourceFromMapperTokenType(fieldMapperConfig.mapsFromType);
      this.setTransitionSource(transition, sourcePTDVId, transitionSource);

      const fieldPairKey = getFieldPairKey({
        sourcePTDVId,
        sourceSectionType: fromFieldSectionType,
        sourceFieldClientId: fromFieldClientId,
        targetPTDVId,
        targetSectionType: toFieldSectionType,
        targetFieldClientId: toFieldClientId,
      });

      const customMappingData = this.buildCustomMappingData(fieldMapperConfig, fromFieldId);
      transition.customMappingDataByFieldPair[fieldPairKey] = customMappingData;
    }

    return edge;
  }

  private buildCustomMappingData(
    fieldMapperConfig: FieldMapperConfigGQL | TopLevelFieldMapperConfigGQL,
    fromFieldId: string
  ): CustomMappingData | null {
    const customMappingData: CustomMappingData = {};
    if (fieldMapperConfig.resultMustBeNonNullToMap) {
      customMappingData.resultMustBeNonNullToMap = true;
    }
    if (isLookupConfigCustomMapping(fieldMapperConfig.lookupConfig, fromFieldId)) {
      customMappingData.lookupConfig = {
        isMulti: fieldMapperConfig.lookupConfig.isMulti,
        lookupSteps: fieldMapperConfig.lookupConfig.lookupSteps,
        multiFlattenLevel: fieldMapperConfig.lookupConfig.multiFlattenLevel,
      };
    }
    return isCustomMappingDataEmpty(customMappingData) ? null : customMappingData;
  }

  private setTransitionSource(
    transition: Transition,
    sourcePTDVId: string,
    transitionSource: TransitionSource
  ): void {
    const existingSource = transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
    if (existingSource != null && existingSource !== transitionSource) {
      assert(false, 'Expected existing transition source to match inferred transition source', {
        existingSource,
        transitionSource,
        sourcePTDVId,
      });
    }
    transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = transitionSource;
  }

  private getUpstreamMappingInfoFromFieldMapperConfig(
    fieldMapperConfig: FieldMapperConfigGQL | TopLevelFieldMapperConfigGQL
  ): {
    fromFieldSectionType: TaskSchemaNodeSectionType;
    fromFieldId: string;
  } | null {
    const fromTaskDefinitionVersion = fieldMapperConfig.fromProcessTaskDefinitionVersion;
    if (fromTaskDefinitionVersion == null || isInaccessible(fromTaskDefinitionVersion)) {
      return null;
    }
    const inputFieldDefinitions = fromTaskDefinitionVersion.inputFieldDefinitions ?? [];
    const outputFieldDefinitions = fromTaskDefinitionVersion.outputFieldDefinitions ?? [];

    // Examine lookup steps to find the field this edge is mapping from. Workflows after the introduction
    // of task definitions will have a PROCESS_TASK_DEFINITION_FIELD lookup step, while Workflows created
    // before that change will have a SCHEMA_FIELD lookup step.
    const fromFieldId = getFirstFieldIdFromLookupConfig(fieldMapperConfig.lookupConfig);
    if (fromFieldId == null) {
      return null;
    }

    const isFromFieldInFieldDefinitions = (fieldDefinitions: Array<FieldDefinitionGraphQL>): boolean =>
      fieldDefinitions.some((fieldDefinition) => fieldDefinition.fieldIdentifier === fromFieldId);

    const throwCouldNotDetermineSectionError = (): never => {
      assert(false, `Could not determine the section of the source field`, {
        fieldMapperConfig,
      });
    };

    let fromFieldSectionType!: TaskSchemaNodeSectionType;
    const lookupSteps: Array<LookupStepSpec> = fieldMapperConfig.lookupConfig.lookupSteps;
    if (lookupSteps.some((lookupStep) => lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK)) {
      return {
        fromFieldSectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
        fromFieldId,
      };
    }
    if (fieldMapperConfig.mapsFromType === ProcessMapperTokenType.PROCESS_OUTPUT) {
      if (lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        assert(
          isFromFieldInFieldDefinitions(inputFieldDefinitions),
          'Expected the source field to be a valid field in the task schema.'
        );
        fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
      } else if (isFromFieldInFieldDefinitions(outputFieldDefinitions)) {
        fromFieldSectionType = TaskSchemaNodeSectionType.OUTPUTS;
      } else {
        throwCouldNotDetermineSectionError();
      }
    } else if (isFromFieldInFieldDefinitions(inputFieldDefinitions)) {
      fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
    } else {
      throwCouldNotDetermineSectionError();
    }

    return {fromFieldSectionType, fromFieldId};
  }

  /**
   * Creates transitions for top-level schemaMapperConfigs that are not referenced by any node's
   * inboundSchemaMappers. These represent optional transitions (no control flow edges) that the
   * old editor stored in the top-level list but not in the template flowchart.
   */
  private buildTransitionsFromUnreferencedSchemaMapperConfigs(): void {
    if (this.flowchartConfigVersion?.templateFlowchart == null) {
      return;
    }

    const referencedConfigIds = new Set<string>();
    for (const nodeConfig of this.flowchartConfigVersion.templateFlowchart.nodeConfigs) {
      for (const inboundSchemaMapper of nodeConfig.inboundSchemaMappers) {
        if (inboundSchemaMapper.schemaMapperConfig?.id) {
          referencedConfigIds.add(inboundSchemaMapper.schemaMapperConfig.id);
        }
      }
    }

    for (const schemaMapperConfig of this.flowchartConfigVersion.schemaMapperConfigs) {
      if (referencedConfigIds.has(schemaMapperConfig.id)) {
        continue;
      }
      if (isInaccessible(schemaMapperConfig.toProcessTaskDefinitionVersion)) {
        continue;
      }

      const targetPTDVId = schemaMapperConfig.toProcessTaskDefinitionVersion.id;
      const sourceInfos: Array<{ptdvId: string; mapsFromType: ProcessMapperTokenType}> = [];
      for (const sourceConfig of schemaMapperConfig.schemaMapperSourceConfigs) {
        if (!isInaccessible(sourceConfig.processTaskDefinitionVersion)) {
          sourceInfos.push({
            ptdvId: sourceConfig.processTaskDefinitionVersion.id,
            mapsFromType: sourceConfig.mapsFromType,
          });
        }
      }

      if (sourceInfos.length === 0) {
        continue;
      }

      const sourcePTDVIds = sourceInfos.map((info) => info.ptdvId);
      const transition = this.getOrCreateTransition(sourcePTDVIds, targetPTDVId);

      for (const {ptdvId, mapsFromType} of sourceInfos) {
        const transitionSource = this.getTransitionSourceFromMapperTokenType(mapsFromType);
        this.setTransitionSource(transition, ptdvId, transitionSource);
      }

      this.populateFieldMappingsFromUnreferencedMapperConfig(schemaMapperConfig, transition);
    }
  }

  /**
   * Reads field mapper configs from an unreferenced schema mapper config and populates the
   * transition's customMappingDataByFieldPair so field-level mappings round-trip through save.
   */
  private populateFieldMappingsFromUnreferencedMapperConfig(
    schemaMapperConfig: ProcessFlowchartConfigVersionGQL['schemaMapperConfigs'][number],
    transition: Transition
  ): void {
    if (isInaccessible(schemaMapperConfig.toProcessTaskDefinitionVersion)) {
      return;
    }

    // Register field definitions from unreferenced PTDVs so fieldIdentifierToFieldClientId
    // can resolve them when building field pair keys.
    this.registerFieldDefinitionsFromPTDV(schemaMapperConfig.toProcessTaskDefinitionVersion);
    for (const fieldMapperConfig of schemaMapperConfig.schemaFieldMapperConfigs) {
      if (!isInaccessible(fieldMapperConfig.fromProcessTaskDefinitionVersion)) {
        this.registerFieldDefinitionsFromPTDV(fieldMapperConfig.fromProcessTaskDefinitionVersion);
      }
    }

    const targetPTDVId = schemaMapperConfig.toProcessTaskDefinitionVersion.id;
    const toFieldSectionType =
      schemaMapperConfig.mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
        ? TaskSchemaNodeSectionType.OUTPUTS
        : TaskSchemaNodeSectionType.INPUTS;

    for (const fieldMapperConfig of schemaMapperConfig.schemaFieldMapperConfigs) {
      const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(fieldMapperConfig);
      if (fromFieldInfo == null) {
        continue;
      }

      const sourcePTDVId = isInaccessible(fieldMapperConfig.fromProcessTaskDefinitionVersion)
        ? null
        : fieldMapperConfig.fromProcessTaskDefinitionVersion.id;
      if (sourcePTDVId == null) {
        continue;
      }

      const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
      const fromFieldClientId = this.fieldIdentifierToFieldClientId[fromFieldId] ?? fromFieldId;
      const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;
      const toFieldClientId = this.fieldIdentifierToFieldClientId[toFieldId] ?? toFieldId;

      const fieldPairKey = getFieldPairKey({
        sourcePTDVId,
        sourceSectionType: fromFieldSectionType,
        sourceFieldClientId: fromFieldClientId,
        targetPTDVId,
        targetSectionType: toFieldSectionType,
        targetFieldClientId: toFieldClientId,
      });

      const customMappingData = this.buildCustomMappingData(fieldMapperConfig, fromFieldId);
      transition.customMappingDataByFieldPair[fieldPairKey] = customMappingData;
    }
  }

  private registerFieldDefinitionsFromPTDV(ptdv: {
    inputFieldDefinitions?: ReadonlyArray<{fieldIdentifier: string; fieldClientIdentifier: string}> | null;
    outputFieldDefinitions?: ReadonlyArray<{fieldIdentifier: string; fieldClientIdentifier: string}> | null;
  }): void {
    for (const fieldDef of ptdv.inputFieldDefinitions ?? []) {
      if (this.fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] == null) {
        this.fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
      }
    }
    for (const fieldDef of ptdv.outputFieldDefinitions ?? []) {
      if (this.fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] == null) {
        this.fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
      }
    }
  }

  private setFallbackTransitionSources(): void {
    for (const node of this.nodesStaticData) {
      if (!isStaticTaskSchemaNodeData(node)) {
        continue;
      }

      const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((n) => ({id: n.id, data: n})), edges: this.edgesStaticData},
        node.id
      );

      for (const downstreamMappableNodeId of downstreamMappableNodeIds) {
        const downstreamNode = this.getTaskSchemaNodeById(downstreamMappableNodeId);
        if (!downstreamNode) {
          continue;
        }

        const sourcePTDVId = node.taskDefinitionVersionSpec.clientId;
        const targetPTDVId = downstreamNode.taskDefinitionVersionSpec.clientId;

        const transition = this.getOrCreateTransition([sourcePTDVId], targetPTDVId);

        // Set default source if not already set by a mapper config
        if (transition.transitionSourcesBySourcePTDVId[sourcePTDVId] == null) {
          const defaultSource = getDefaultTransitionSource(node.id, this.nodesStaticData);
          transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = defaultSource;
        }
      }
    }
  }

  /**
   * Marks transitions as optional (isPlanned = false) if their PTDV pairs are not
   * connected by control flow edges in the flowchart.
   */
  private markOptionalTransitions(): void {
    const connectedPTDVPairs = new Set<string>();
    for (const node of this.nodesStaticData) {
      if (!isStaticTaskSchemaNodeData(node) || !isMappableNode({id: node.id, data: node})) {
        continue;
      }
      const upstreamIds = findAllUpstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((n) => ({id: n.id, data: n})), edges: this.edgesStaticData},
        node.id
      );
      for (const upstreamId of upstreamIds) {
        const upstreamPTDV = this.nodeClientIdToTaskDefinitionVersion[upstreamId]?.id;
        const nodePTDV = this.nodeClientIdToTaskDefinitionVersion[node.id]?.id;
        if (upstreamPTDV && nodePTDV) {
          connectedPTDVPairs.add(`${upstreamPTDV}:${nodePTDV}`);
        }
      }
    }

    for (const transition of Object.values(this.transitions)) {
      const allPairsConnected = transition.sourcePTDVIds.every((sourcePTDVId) =>
        connectedPTDVPairs.has(`${sourcePTDVId}:${transition.targetPTDVId}`)
      );
      if (!allPairsConnected) {
        transition.isPlanned = false;
      }
    }
  }

  private getTransitionSourceFromMapperTokenType(mapperTokenType: ProcessMapperTokenType): TransitionSource {
    return mapperTokenType === ProcessMapperTokenType.PROCESS_OUTPUT
      ? TransitionSource.OUTPUTS
      : TransitionSource.INPUTS;
  }
  private updateNodesWithMapFromStartFields(): void {
    const rootNodeData = this.getRootNode();
    if (!rootNodeData) {
      throw new Error('No root node found');
    }
    populateMapFromStartSectionRows(this.nodesStaticData, rootNodeData, this.mapFromStartFieldIdsByNode);
  }

  private getDidInitialConfigHaveConditionalMapping(): boolean {
    return (this.flowchartConfigVersion?.schemaMapperConfigs ?? []).some((config) =>
      config.schemaFieldMapperConfigs.some((fmc) => fmc.resultMustBeNonNullToMap)
    );
  }
}

export default function buildStaticDataFromFlowchartConfigGraphQL(
  args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs
): StaticFlowchartData {
  const runner = new BuildStaticDataFromFlowchartConfigGraphQLRunner(args);
  return runner.run();
}
